import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import socketService from '../services/socketService';
import { reset as resetAction, start as startAction, updateFromServer as updateAction, complete as completeAction } from '../store/slices/progressSlice';

const useProgress = (onComplete) => {
  const dispatch = useDispatch();
  const { progress, status, steps, currentStep, isActive, isAnimating } = useSelector(state => state.progress);
  const hasCompletedRef = useRef(false);
  const sessionIdRef = useRef(null);
  const websocketRef = useRef(null);

  const resetProgress = useCallback(() => {
    hasCompletedRef.current = false;
    dispatch(resetAction());
  }, [dispatch]);

  const startProgress = useCallback((initialSteps = []) => {
    hasCompletedRef.current = false;
    const socketId = socketService.getSocketId();
    dispatch(startAction({ steps: initialSteps, socketId }));
  }, [dispatch]);

  const updateProgress = useCallback((updates) => {
    dispatch(updateAction(updates));
  }, [dispatch]);

  const completeProgress = useCallback(() => {
    if (hasCompletedRef.current) {
      return;
    }
    hasCompletedRef.current = true;
    dispatch(completeAction());
    if (onComplete) {
      // Let UI settle; mimic previous behavior lightly without timers in reducers
      setTimeout(() => onComplete(), 0);
    }
    
    // Auto-reset after 2 seconds to clear the 100% state
    setTimeout(() => {
      console.log('🔄 [useProgress] Auto-resetting progress after completion');
      dispatch(resetAction());
    }, 2000);
  }, [dispatch, onComplete]);

  // No socket subscriptions here; App-level listeners update Redux to avoid churn
  useEffect(() => {
    // Reset completion guard when progress resets to 0
    if (progress === 0) {
      hasCompletedRef.current = false;
    }
  }, [progress]);

  useEffect(() => {
    console.log('📊 [useProgress] Redux-driven progress state changed:', {
      progress,
      status,
      currentStep,
      isActive,
      isAnimating
    });
  }, [progress, status, currentStep, isActive, isAnimating]);

  // Get current socket ID for components that need it
  const getSocketId = useCallback(() => {
    return socketService.getSocketId();
  }, []);

  // Keep old name for backward compatibility
  const getSessionId = useCallback(() => {
    return socketService.getSessionId();
  }, []);

  return {
    progress,
    status,
    steps,
    currentStep,
    isActive,
    isAnimating,
    resetProgress,
    startProgress,
    updateProgress,
    completeProgress,
    getSocketId,
    getSessionId
  };
};

export default useProgress;
